// Is this page MSAL's hidden iframe (or popup) bringing a sign-in answer back?
// Pure, so it is tested here rather than discovered on a device.
//
// ⚠ WHY THIS FILE EXISTS. msal-browser v5 changed how a silent renewal gets its
// answer back. The hidden iframe is sent to the redirect URI — this app's own
// origin (config/auth.ts) — and the PARENT no longer reads the iframe's URL. It
// waits on a BroadcastChannel for the page there to call
// `broadcastResponseToMainFrame`. The Mail.Read popup (services/graphMail.ts)
// waits on the same channel. Until 2026-10-02 this app never made that call and
// rendered itself in whatever window loaded it. That day the Quality app, built
// the same way, met a session expired by conditional access (AADSTS70044): the
// whole SPA booted inside the hidden iframe, found the cached account, asked for
// a token and opened ANOTHER hidden iframe — about 20 nested levels in 30
// seconds, until Entra stopped answering (AADSTS50196, request loop).
//
// The other half of that incident does not apply here. The Quality app only
// redirected to sign in on interaction_required or a code containing "timeout",
// and v5's bridge timeout is `timed_out` (sub-error `redirect_bridge_timeout`).
// acquireApiToken here redirects on ANY silent failure, so that error already
// reaches sign-in — config/__tests__/auth.test.ts pins it.

/** The shape of `window` this needs — narrow so a test can hand it a plain object. */
export interface FrameLike {
  self: unknown;
  top: unknown;
  opener: unknown;
  location: { hash: string; search: string };
}

/** True when the URL carries an authorization response: a `state`, plus a `code`
 *  or an `error`. MSAL asks for the fragment response mode, but a query response
 *  is checked too because that choice is the library's, not ours.
 *  `code` holds because config/auth.ts leaves `protocolMode` at its default (AAD,
 *  verified against the installed v5.17.3). EAR mode answers with `ear_jwe`
 *  instead, and this would not see it. */
export function hasAuthResponse(hash: string, search: string): boolean {
  for (const raw of [hash, search]) {
    const params = new URLSearchParams(raw.replace(/^[#?]/, ''));
    if (params.has('state') && (params.has('code') || params.has('error'))) return true;
  }
  return false;
}

/**
 * True when this page is MSAL's hidden iframe (or a popup) landing on the
 * redirect URI with an answer. That page's only job is to hand the answer to the
 * window that asked for it. It must never render the app: an app rendered there
 * has a signed-in account, asks for a token, and opens the next hidden iframe.
 *
 * A top-level page with a response in the URL is the ordinary sign-in redirect
 * coming home, and `handleRedirectPromise` in main.tsx deals with that.
 */
export function isMsalChildWindow(win: FrameLike): boolean {
  const inFrame = win.self !== win.top;
  const inPopup = Boolean(win.opener);
  return (inFrame || inPopup) && hasAuthResponse(win.location.hash, win.location.search);
}
